import { useEffect, useRef, useState } from 'react';
import { useDialogA11y } from '../../lib/use-dialog-a11y.js';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { X, Camera as CameraIcon, Layers, Minus, Plus, Search } from 'lucide-react';
import { api, type InventoryItemSummary } from '../../lib/api.js';
import { useFieldJob } from '../../lib/field-job.js';
import { BarcodeScanner } from '../BarcodeScanner.js';

interface PartsPickerProps {
  open: boolean;
  /** Customer's default warehouse for stock lookup. Falls back to '00'. */
  defaultWhse: string;
  /** The tech's truck warehouse. When set, searches and scans resolve against the van first so picks relieve van stock. */
  truckWhse?: string | null;
  onClose: () => void;
  /**
   * Called when the user picks an inventory item. The picker dispatches
   * addPart locally; the caller can kick off the Spire write here.
   */
  onPart: (input: {
    clientId: string;
    partNo: string;
    description: string;
    whse: string;
    qty: string;
    unitPrice: string;
    uom: string;
  }) => void;
}

const RECENT_KEY = 'tinsmith:field-recent-parts';
const RECENT_MAX = 8;

interface RecentPart {
  partNo: string;
  description: string;
  whse: string;
  unitPrice: string;
  uom: string;
}

function readRecent(): RecentPart[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RecentPart[]) : [];
  } catch {
    return [];
  }
}

function writeRecent(list: RecentPart[]): void {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    /* storage full — silent */
  }
}

/**
 * Inline parts picker — modal overlay opened from the on-site Parts tile.
 * Matches the .pscan layout from Design/hvac_interactive_field_worker.html.
 *
 * Three input methods:
 *   1. Type a part number / search by description
 *   2. Open the barcode scanner (reuses BarcodeScanner)
 *   3. Tap a row from the recent-parts list (localStorage)
 */
export function PartsPicker({ open, defaultWhse, truckWhse, onClose, onPart }: PartsPickerProps) {
  const addPart = useFieldJob((s) => s.addPart);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [recent, setRecent] = useState<RecentPart[]>(() => readRecent());
  // Batch-scan state. When true, the scanner stays open after each
  // decode and adds the resolved part to the visit. The toast keeps the
  // tech informed without leaving the camera view.
  const [batchMode, setBatchMode] = useState(false);
  const [batchCount, setBatchCount] = useState(0);
  const [batchToast, setBatchToast] = useState<{ kind: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  // Tracks the in-flight batch lookup so we don't fire two onResult calls
  // racing against each other (e.g. tech twitched and got two scans before
  // the first inventory.search resolved).
  const batchBusyRef = useRef(false);

  // Reset search when re-opened.
  useEffect(() => {
    if (open) {
      setQ('');
      setDebouncedQ('');
      setRecent(readRecent());
      setBatchMode(false);
      setBatchCount(0);
      setBatchToast(null);
      batchBusyRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const searchQuery = useQuery({
    queryKey: ['field', 'parts-search', truckWhse ?? '', debouncedQ],
    // Truck first: the van's item records carry the van's on-hand, and a
    // pick from them relieves van stock on the SO line. Anything the van
    // doesn't carry falls back to a company-wide search.
    // Both searches fire at once — on FPS each Spire inventory query costs
    // 1.5-2 s of server time (2026-09-26), so running them back-to-back
    // doubled the wait on every keystroke.
    queryFn: async ({ signal }) => {
      if (!truckWhse) return api.inventory.search({ q: debouncedQ, limit: 8 }, signal);
      const [onTruck, anywhere] = await Promise.all([
        api.inventory.search({ whse: truckWhse, q: debouncedQ, limit: 8 }, signal),
        api.inventory.search({ q: debouncedQ, limit: 8 }, signal),
      ]);
      return onTruck.length > 0 ? onTruck : anywhere;
    },
    enabled: debouncedQ.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // Must stay ABOVE the early return — a hook below it crashes the picker
  // on open ("Rendered more hooks than during the previous render").
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose, open);

  if (!open) return null;

  const choose =(item: InventoryItemSummary, qty: number) => {
    const whse = item.whseCode || defaultWhse || '00';
    const unitPrice = item.defaultSellPrice ?? '0';
    const uom = item.uom ?? 'EA';
    const description = item.description ?? item.partNo;
    // Coerce qty defensively — the stepper clamps at 1 but a stale closure
    // could in theory deliver something odd. One line per part regardless
    // of qty so we don't blow the SO up with N rows for N units.
    const qtyStr = String(Math.max(1, Math.floor(qty)));
    const clientId = addPart({
      partNo: item.partNo,
      description,
      whse,
      qty: qtyStr,
      unitPrice,
      uom,
    });
    onPart({ clientId, partNo: item.partNo, description, whse, qty: qtyStr, unitPrice, uom });

    // Surface to "recent" so the next on-site visit lands faster.
    const next = [
      { partNo: item.partNo, description, whse, unitPrice, uom },
      ...recent.filter((r) => r.partNo !== item.partNo),
    ].slice(0, RECENT_MAX);
    setRecent(next);
    writeRecent(next);

    onClose();
  };

  const chooseRecent = (r: RecentPart, qty: number) => {
    const qtyStr = String(Math.max(1, Math.floor(qty)));
    const clientId = addPart({
      partNo: r.partNo,
      description: r.description,
      whse: r.whse,
      qty: qtyStr,
      unitPrice: r.unitPrice,
      uom: r.uom,
    });
    onPart({ clientId, ...r, qty: qtyStr });
    onClose();
  };

  const handleScan = (text: string) => {
    setScanOpen(false);
    setQ(text);
    setDebouncedQ(text.trim());
  };

  /**
   * Batch-mode scan handler. Resolves the barcode to an inventory item
   * via a 1-result search, adds it to the visit if found, and surfaces
   * a toast so the tech sees the outcome without leaving the camera.
   *
   * If the search returns nothing (no inventory match) or multiple
   * candidates, we surface a "Check this" warn toast — the tech can
   * then tap Done and use the typed-search path to disambiguate.
   */
  const handleBatchScan = (text: string) => {
    if (batchBusyRef.current) return; // ignore overlapping decodes
    batchBusyRef.current = true;
    void (async () => {
      try {
        const trimmed = text.trim();
        const [onTruck, anywhere] = await Promise.all([
          truckWhse ? api.inventory.search({ whse: truckWhse, partNo: trimmed, limit: 2 }) : Promise.resolve([]),
          api.inventory.search({ partNo: trimmed, limit: 2 }),
        ]);
        const results = onTruck.length > 0 ? onTruck : anywhere;
        // Auto-add ONLY on an exact partNo match. A fuzzy single-result
        // hit (e.g. scanner produced a vendor prefix that drops one
        // candidate) is too risky to silently add — surface a warn so
        // the tech disambiguates manually. Same goes for >1 candidates.
        const exact = results.find((r) => r.partNo === trimmed);
        if (!exact) {
          if (results.length === 0) {
            setBatchToast({ kind: 'warn', text: `No inventory match for "${text}". Add manually.` });
          } else if (results.length === 1) {
            setBatchToast({
              kind: 'warn',
              text: `Closest match "${results[0]!.partNo}" — doesn't match scanned code. Confirm manually.`,
            });
          } else {
            setBatchToast({
              kind: 'warn',
              text: `${results.length} candidates for "${text}". Add manually.`,
            });
          }
          return;
        }
        const whse = exact.whseCode || defaultWhse || '00';
        const unitPrice = exact.defaultSellPrice ?? '0';
        const uom = exact.uom ?? 'EA';
        const description = exact.description ?? exact.partNo;
        const clientId = addPart({
          partNo: exact.partNo,
          description,
          whse,
          qty: '1',
          unitPrice,
          uom,
        });
        onPart({ clientId, partNo: exact.partNo, description, whse, qty: '1', unitPrice, uom });
        setBatchCount((c) => c + 1);
        setBatchToast({ kind: 'ok', text: `${exact.partNo} — ${description}` });
        // Bump to the recents list too so post-batch typing leans on it.
        const next = [
          { partNo: exact.partNo, description, whse, unitPrice, uom },
          ...recent.filter((r) => r.partNo !== exact.partNo),
        ].slice(0, RECENT_MAX);
        setRecent(next);
        writeRecent(next);
      } catch (err) {
        setBatchToast({
          kind: 'error',
          text: err instanceof Error ? err.message : 'Lookup failed',
        });
      } finally {
        batchBusyRef.current = false;
      }
    })();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add part to job"
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-end md:items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-xl shadow-xl overflow-hidden"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <span className="text-sm font-medium text-ink">Add part to job</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close picker"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="p-4 space-y-3">
          {/* Search input + scan button */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" aria-hidden />
            <input
              autoFocus
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Part # or description"
              className="h-11 w-full rounded-md border bg-surface pl-9 pr-20 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand"
              style={{ borderColor: 'var(--color-border-secondary)' }}
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => {
                  setBatchMode(true);
                  setBatchCount(0);
                  setBatchToast(null);
                  setScanOpen(true);
                }}
                aria-label="Batch scan multiple barcodes"
                title="Batch scan — keep the camera open and add each scan"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brand hover:bg-brand-50 active:bg-brand-100"
              >
                <Layers className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setBatchMode(false);
                  setScanOpen(true);
                }}
                aria-label="Scan barcode"
                title="Single scan — fills the search box"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brand hover:bg-brand-50 active:bg-brand-100"
              >
                <CameraIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Search results */}
          {debouncedQ.length >= 2 && (
            <div>
              {searchQuery.isPending && (
                <div className="text-xs text-ink-muted py-2">Searching Spire…</div>
              )}
              {searchQuery.isError && (
                <div className="rounded-md border border-red-300 bg-red-50 px-2.5 py-2 text-[12px] text-red-900">
                  Lookup failed: {searchQuery.error instanceof Error ? searchQuery.error.message : 'Spire unreachable'}.
                  <button
                    type="button"
                    onClick={() => void searchQuery.refetch()}
                    className="ml-2 underline font-medium"
                  >
                    Retry
                  </button>
                </div>
              )}
              {!searchQuery.isError && searchQuery.data && searchQuery.data.length === 0 && (
                <div className="text-xs text-ink-muted py-2">
                  No matches for <strong>{debouncedQ}</strong>.
                </div>
              )}
              {searchQuery.data && searchQuery.data.length > 0 && (
                <ul className="space-y-1">
                  {searchQuery.data.map((item) => (
                    <li key={item.spireId}>
                      <PickableRow
                        title={item.description ?? item.partNo}
                        meta={`${item.partNo} · ${item.whseCode === truckWhse ? 'on truck' : `whse ${item.whseCode}`}: ${item.onHandQty}${item.uom ? ` ${item.uom}` : ''} · $${item.defaultSellPrice}`}
                        onAdd={(qty) => choose(item, qty)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Recent parts */}
          {recent.length > 0 && debouncedQ.length < 2 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">
                Recently used on jobs
              </div>
              <ul className="space-y-1">
                {recent.map((r) => (
                  <li key={r.partNo}>
                    <PickableRow
                      title={r.description}
                      meta={`${r.partNo} · $${r.unitPrice}`}
                      onAdd={(qty) => chooseRecent(r, qty)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {debouncedQ.length < 2 && recent.length === 0 && (
            <div className="text-xs text-ink-muted py-2 text-center">
              Type to search or tap the camera to scan a barcode.
            </div>
          )}
        </div>
      </div>

      <BarcodeScanner
        open={scanOpen}
        batch={batchMode}
        batchCount={batchCount}
        batchToast={batchToast}
        onResult={batchMode ? handleBatchScan : handleScan}
        onClose={() => {
          setScanOpen(false);
          setBatchMode(false);
        }}
      />
    </div>
  );
}

/**
 * One pickable row in the parts picker (typed search OR recent). Owns its
 * own qty state so each row's count is independent — a tech can bump the
 * qty on Filter A to 3, then tap Add Filter B with default qty 1.
 *
 * Stepper has -/+ buttons that stopPropagation so they don't trigger the
 * row's main Add action. Default qty = 1, clamped at 1..99 (anything
 * higher is almost certainly a typo on a thumb-sized stepper).
 */
function PickableRow({
  title,
  meta,
  onAdd,
}: {
  title: string;
  meta: string;
  onAdd: (qty: number) => void;
}) {
  const [qty, setQty] = useState(1);
  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(99, q + 1));
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onAdd(qty)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onAdd(qty);
        }
      }}
      className="w-full grid grid-cols-[1fr_auto_auto] items-center gap-2 rounded-md border bg-surface px-2.5 py-2 text-left transition-shadow hover:shadow-sm hover:border-line-soft cursor-pointer"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink truncate">{title}</div>
        <div className="text-[11px] font-mono text-ink-muted truncate">{meta}</div>
      </div>
      <div
        className="inline-flex items-stretch rounded-md border overflow-hidden bg-surface shrink-0"
        style={{ borderColor: 'var(--color-border-tertiary)' }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dec}
          disabled={qty <= 1}
          aria-label="Decrease quantity"
          className="inline-flex h-7 w-7 items-center justify-center text-ink-muted hover:bg-surface-muted disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Minus className="h-3 w-3" aria-hidden />
        </button>
        <span className="px-2 inline-flex items-center text-xs font-medium tabular-nums min-w-[28px] justify-center border-l border-r" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          {qty}
        </span>
        <button
          type="button"
          onClick={inc}
          disabled={qty >= 99}
          aria-label="Increase quantity"
          className="inline-flex h-7 w-7 items-center justify-center text-ink-muted hover:bg-surface-muted disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Plus className="h-3 w-3" aria-hidden />
        </button>
      </div>
      <Plus className="h-4 w-4 text-ink-accent shrink-0" aria-hidden />
    </div>
  );
}
